import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { t, getLocale } from './i18n';
import {
  conecteazaFacebook,
  conecteazaTikTok,
  conecteazaLinkedIn,
  conecteazaThreads,
  conecteazaX,
  deconecteazaRetea,
  CLIENT_KEY_TIKTOK,
  CLIENT_ID_LINKEDIN,
  CLIENT_ID_THREADS,
  CLIENT_ID_X,
} from './conectare';
import DrepturiClient from './DrepturiClient';
import InvitaContClient from './InvitaContClient';
import TelefonClient from './TelefonClient';
import { CheieClaude } from './CheieClaude';

// ============================================================
// Pagina clientului, refacuta (16 septembrie 2026, PAGINA_CLIENT_PLAN.md,
// Etapa 4, pasul 6). Luminita, despre pagina de dinainte: „oribila si old".
//
// Ce se schimba acum, in prima repriza: antetul (nume, functie, localitate, fara
// poza mare a omului — se vede in pozele conturilor) si RANDUL DE CONTURI, ca in
// Publer: poza contului in cerc, insigna retelei jos-stanga, bulina de stare
// sus-dreapta, numele contului sub poza, iar la clic un meniu cu ce se poate face.
//
// Postarile cu tab-uri si banda „De stiut" vin in repriza urmatoare; pana atunci
// pagina veche ramane dedesubt (`children`), fara antetul ei, ca sa nu se piarda
// nimic intre reprize.
//
// Datele vin dintr-o singura cerere, `conturi_client`, care verifica cine
// intreaba si intoarce si starea: ok / expira / expirat.
// ============================================================

const ORDINE_RETELE = ['facebook', 'instagram', 'tiktok', 'linkedin', 'threads', 'x'];

// Tab-urile cerute de Luminita: „poate uit de postare in asteptarea aprobarii, ma
// apuc de alt client, asa le am pe toate aici". „Toate" e deschis implicit.
// Cele doua directii ale aprobarii nu se mai amesteca (20 septembrie 2026). Pentru AGENTIE,
// „De aprobat" ramane tot ce asteapta pe cineva: si ce a trimis clientului spre aprobare
// finala, si ce i-a trimis clientul ei. Pentru CLIENT, „De aprobat" e doar ce trebuie sa
// aprobe EL, iar ce a trimis el are tabul lui, „Trimise la aprobare". Pana acum clientul isi
// primea propriile postari in lista lui de aprobat si era rugat sa se aprobe singur — de aici
// si nedumerirea unui om nou intrat in aplicatie.
function taburi(esteClient) {
  return [
    { id: 'toate',      cheie: 'tabToate',      statusuri: null },
    { id: 'de_aprobat', cheie: 'tabDeAprobat',  statusuri: esteClient ? ['aprobare_finala'] : ['aprobare_finala', 'intern'] },
    ...(esteClient ? [{ id: 'trimise', cheie: 'tabTrimise', statusuri: ['intern'] }] : []),
    // Aprobate de client, asteapta ca agentia sa le programeze sau sa le publice (16 sept 2026).
    { id: 'aprobate',   cheie: 'tabAprobate',   statusuri: ['aprobat'] },
    { id: 'programate', cheie: 'tabProgramate', statusuri: ['programat'] },
    { id: 'ciorne',     cheie: 'tabCiorne',     statusuri: ['draft'] },
    { id: 'publicate',  cheie: 'tabPublicate',  statusuri: ['publicat'] },
    { id: 'respinse',   cheie: 'tabRespinse',   statusuri: ['respins'] },
  ];
}

// Un rand = un GRUP scris impreuna in compozitorul nou (`grupId`). Postarile
// vechi, fara grup, sunt fiecare randul ei.
function grupeaza(postari) {
  const grupuri = new Map();
  postari.forEach(p => {
    const cheie = p.grupId || `singura-${p.id}`;
    if (!grupuri.has(cheie)) grupuri.set(cheie, []);
    grupuri.get(cheie).push(p);
  });
  return [...grupuri.values()];
}

// Data dupa care se aseaza randurile: ce s-a publicat, cand s-a publicat; restul,
// dupa data programata. Fara data, la coada.
function momentulGrupului(grup) {
  const momente = grup.map(p => {
    if (p.publicatLa) return new Date(p.publicatLa).getTime();
    if (p.dataProgramata) return new Date(`${p.dataProgramata}T${(p.ora || '00:00').slice(0, 5)}`).getTime();
    return 0;
  });
  return Math.max(...momente);
}

// CIORNELE se aseaza altfel: dupa cand au fost scrise, cele mai noi sus (6 octombrie 2026,
// cerut de Luminita). O ciorna n-are, de obicei, data programata, deci dupa regula de mai sus
// cadea la coada, sub ciorne vechi care poarta inca o marca de publicare ramasa de la o
// incercare trecuta (`publicat_la` nu se sterge cand postarea se intoarce la ciorna). Asa au
// ajuns nevazute, la capatul listei, primele ciorne puse prin priza.
function momentulCrearii(grup) {
  return Math.max(...grup.map(p => (p.creatLa ? new Date(p.creatLa).getTime() : 0)));
}

// Prima fraza, ca in NM Write: atat cat sa recunosti postarea dintr-o privire.
function primaFraza(text) {
  if (!text || !text.trim()) return null;
  const curat = text.trim().replace(/\s+/g, ' ');
  const taiere = curat.search(/[.!?]\s/);
  return taiere > 20 ? curat.slice(0, taiere + 1) : curat;
}

const ETICHETE = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  linkedin: 'LinkedIn',
  threads: 'Threads',
  x: 'X',
};

function textStare(stare, expiraLa) {
  if (stare === 'expirat') return t('paginaClient', 'stareExpirat');
  if (stare === 'expira') {
    const zile = expiraLa ? Math.max(0, Math.ceil((new Date(expiraLa) - Date.now()) / 86400000)) : null;
    return zile === null ? t('paginaClient', 'stareExpira') : `${t('paginaClient', 'stareExpira')} (${zile})`;
  }
  return t('paginaClient', 'stareOk');
}

function PozaCont({ cont, retea }) {
  if (!cont) {
    return <div className="cont-gol">+</div>;
  }
  if (cont.poza_url) {
    return <img className="cont-poza" src={cont.poza_url} alt={cont.nume || retea} />;
  }
  return <div className="cont-initiala">{(cont.nume || ETICHETE[retea] || '?').charAt(0).toUpperCase()}</div>;
}

// „Sterge" pe randul unei postari, pus inapoi pe 21 septembrie 2026. Butonul a existat pe
// ecranul vechi (`ProfilClient`, in App.js), care a iesit din aplicatie la Etapa 4 si a
// ramas cod mort: functia lui era intreaga, dar nu mai ajungea nimeni la ea. Se da DOAR
// la agentie (super_admin si manager, din App.js); interfata clientului nu-l primeste.
//
// Un rand e un GRUP scris impreuna, deci se sterg toate cardurile lui, cu o intrebare.
// Pe Facebook postarea se sterge si de pe retea; pe celelalte `facebook-delete` raspunde
// `skipped` si postarea ramane acolo — de aceea confirmarea o spune pe fata.
export async function stergeGrupPostari(grup, setPostari, optiuni = {}) {
  const lista = Array.isArray(grup) ? grup : [grup];
  if (!lista.length) return [];
  const cate = lista.length === 1
    ? t('paginaClient', 'oPostare')
    : t('paginaClient', 'nPostari', { n: lista.length });
  const cheie = optiuni.cheieConfirmare || 'confirmSterge';
  if (!window.confirm(t('paginaClient', cheie, { n: cate }))) return [];

  const sterse = [];
  for (const p of lista) {
    // Intai reteaua. Decide FUNCTIA, nu ecranul: ea stie daca postarea are id extern si pe
    // ce retea sta, si raspunde `skipped` cand n-are ce sterge (Instagram, TikTok, ciorne).
    const { data, error } = await supabase.functions.invoke('facebook-delete', { body: { post_id: p.id } });
    if (error || (data && data.error)) {
      // Cazul obisnuit: postarea a fost deja stearsa de pe telefon. Dar daca mai e pe pagina,
      // culegerea o aduce inapoi ca `nativ` — de aceea intrebarea o spune pe fata.
      if (!window.confirm(t('paginaClient', 'confirmDoarLaNoi'))) continue;
    }
    // Apoi la noi, tot prin functie: browserul n-are voie sa stearga din `mesaje`.
    const { data: d2, error: e2 } = await supabase.functions.invoke('facebook-delete', { body: { post_id: p.id, la_noi: true } });
    if (e2 || (d2 && d2.error)) {
      alert(t('paginaClient', 'eroareStergere', { eroare: (d2 && d2.detail) || (e2 && e2.message) || '' }));
      continue;
    }
    sterse.push(p.id);
  }
  if (sterse.length && setPostari) setPostari(prev => prev.filter(x => !sterse.includes(x.id)));
  return sterse;
}

function PaginaClient({
  client,
  inapoiLaClienti,
  iconRetea,
  postari = [],
  statusInfo = {},
  retele = [],
  deschideGrup,
  esteClient = false,
  setPostari = null,
  // Butonul de stergere: vine doar de la agentie (vezi `stergeGrupPostari`).
  onStergeGrup = null,
  // Doar agentia (super_admin, manager) modifica drepturile contului de client.
  onDrepturiSalvate = null,
  // Clientul fara cont primeste butonul de invitatie (24 septembrie 2026). Vine doar din
  // ecranul super_admin, singurul care stie `are_cont`; managerul nu-l primeste inca.
  onInvitat = null,
  // Contul de client deschide pagina direct pe un tab („De aprobat” din meniu).
  tabInitial = 'toate',
  children,
}) {
  const [conturi, setConturi] = useState(null);
  const [drepturiDeschise, setDrepturiDeschise] = useState(false);
  const [invitaDeschisa, setInvitaDeschisa] = useState(false);
  const [telefonDeschis, setTelefonDeschis] = useState(false);
  const [meniuDeschis, setMeniuDeschis] = useState(null);

  const incarcaConturi = useCallback(async () => {
    const { data, error } = await supabase.rpc('conturi_client', { p_client_id: client.id });
    if (error) {
      console.error('Nu am putut citi conturile clientului:', error.message);
      setConturi([]);
      return;
    }
    setConturi(data || []);
  }, [client.id]);

  useEffect(() => { incarcaConturi(); }, [incarcaConturi]);

  // Dupa intoarcerea de la o retea (?facebook=connected etc.), conturile se recitesc:
  // altfel poza noua apare abia la urmatoarea incarcare a paginii.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('facebook') === 'connected' || p.get('tiktok') === 'connected' || p.get('linkedin') === 'connected' || p.get('threads') === 'connected' || p.get('x') === 'connected') {
      window.history.replaceState({}, '', window.location.pathname);
      incarcaConturi();
    }
  }, [incarcaConturi]);

  async function deconecteaza(retea, nume) {
    const intrebare = t('paginaClient', 'confirmDeconectare', { retea: ETICHETE[retea], nume: nume || '' })
      + (retea === 'facebook' ? '\n\n' + t('paginaClient', 'fbIaSiInstagram') : '');
    if (!window.confirm(intrebare)) return;

    // Instagram vine cu Pagina de Facebook, deci pleaca odata cu ea. Altfel
    // interfata ar scrie „neconectat", dar aplicatia ar citi mai departe date.
    const cereri = retea === 'facebook'
      ? [deconecteazaRetea(client.id, 'facebook'), deconecteazaRetea(client.id, 'instagram')]
      : [deconecteazaRetea(client.id, retea)];
    const raspunsuri = await Promise.all(cereri);
    const eroare = raspunsuri.find(r => r.error);
    if (eroare) { alert(t('paginaClient', 'eroareDeconectare') + eroare.error.message); return; }

    setMeniuDeschis(null);
    incarcaConturi();
  }

  function actiuni(retea, cont) {
    const lista = [];
    if (retea === 'facebook') {
      lista.push({ id: 'conecteaza', text: cont ? t('paginaClient', 'schimbaPagina') : t('paginaClient', 'conecteaza'), onClick: () => conecteazaFacebook(client.id) });
      if (cont) lista.push({ id: 'reconecteaza', text: t('paginaClient', 'reconecteaza'), onClick: () => conecteazaFacebook(client.id) });
    }
    // Instagram nu se conecteaza separat: vine cu Pagina de Facebook. Are insa
    // poza si deconectare proprii pe ecran (varianta 1, decisa de Luminita).
    if (retea === 'tiktok' && CLIENT_KEY_TIKTOK) {
      lista.push({ id: 'conecteaza', text: cont ? t('paginaClient', 'reconecteaza') : t('paginaClient', 'conecteaza'), onClick: () => conecteazaTikTok(client.id) });
    }
    if (retea === 'linkedin' && CLIENT_ID_LINKEDIN) {
      lista.push({ id: 'conecteaza', text: cont ? t('paginaClient', 'reconecteaza') : t('paginaClient', 'conecteaza'), onClick: () => conecteazaLinkedIn(client.id) });
    }
    if (retea === 'threads' && CLIENT_ID_THREADS) {
      lista.push({ id: 'conecteaza', text: cont ? t('paginaClient', 'reconecteaza') : t('paginaClient', 'conecteaza'), onClick: () => conecteazaThreads(client.id) });
    }
    if (retea === 'x' && CLIENT_ID_X) {
      lista.push({ id: 'conecteaza', text: cont ? t('paginaClient', 'reconecteaza') : t('paginaClient', 'conecteaza'), onClick: () => conecteazaX(client.id) });
    }
    if (cont) {
      lista.push({ id: 'deconecteaza', text: t('paginaClient', 'deconecteaza'), periculos: true, onClick: () => deconecteaza(retea, cont.nume) });
    }
    return lista;
  }

  const gasitePeRetea = {};
  (conturi || []).forEach(c => { gasitePeRetea[c.platform] = c; });

  const postariClient = postari.filter(p => p.clientId === client.id);

  // Banda „De stiut": doar cand chiar e ceva de stiut (decizia Luminitei).
  // Conturile cu probleme vin din `conturi_client`, iar motivul publicarilor
  // picate din `eroare_publicare`, scris de functiile de publicare la pasul 3.
  const conturiCuProbleme = (conturi || []).filter(c => c.stare && c.stare !== 'ok');
  const postariPicate = postariClient
    .filter(p => p.eroarePublicare)
    .sort((a, b) => new Date(b.eroareLa || 0) - new Date(a.eroareLa || 0))
    .slice(0, 5);
  // Postari plecate al caror prim comentariu n-a plecat (16 septembrie 2026).
  // Motivul contine si textul de pus de mana; randul dispare cand omul confirma.
  const comentariiPicate = postariClient
    .filter(p => p.comentariuEroare && p.status === 'publicat')
    .slice(0, 5);
  const areCevaGrav = conturiCuProbleme.some(c => c.stare === 'expirat') || postariPicate.length > 0;

  async function comentariuPusDeMana(p) {
    const { error } = await supabase.from('postari').update({ comentariu_eroare: null }).eq('id', p.id);
    if (error) { alert(error.message); return; }
    if (setPostari) setPostari(prev => prev.map(x => (x.id === p.id ? { ...x, comentariuEroare: null } : x)));
  }

  // Reconectarea din banda duce la aceeasi autorizare ca meniul contului.
  function actiuneConectare(platform) {
    if (platform === 'facebook' || platform === 'instagram') return () => conecteazaFacebook(client.id);
    if (platform === 'tiktok' && CLIENT_KEY_TIKTOK) return () => conecteazaTikTok(client.id);
    if (platform === 'linkedin' && CLIENT_ID_LINKEDIN) return () => conecteazaLinkedIn(client.id);
    if (platform === 'threads' && CLIENT_ID_THREADS) return () => conecteazaThreads(client.id);
    if (platform === 'x' && CLIENT_ID_X) return () => conecteazaX(client.id);
    return null;
  }

  return (
    <div className="pagina">
      <div className="profil-header">
        {inapoiLaClienti && (
          <button className="btn-inapoi" onClick={inapoiLaClienti}>{t('paginaClient', 'inapoiClienti')}</button>
        )}
        <div className="profil-header-continut">
          <div className="profil-info">
            <div className="profil-nume">{client.nume}</div>
            <div className="profil-functie">
              {[client.functie, client.localitate].filter(Boolean).join(' · ')}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {onInvitat && client.are_cont === false && (
              <button type="button" className="pc-destiut-buton" onClick={() => setInvitaDeschisa(true)}>
                {t('clienti', 'invitaButon')}
              </button>
            )}
            {/* Numarul de WhatsApp (aprobarea prin link, 19.10.2026): tot doar agentia, ca drepturile. */}
            {onDrepturiSalvate && (
              <button type="button" className="pc-destiut-buton" onClick={() => setTelefonDeschis(true)}>
                {t('clienti', 'telefonButon')}
              </button>
            )}
            {onDrepturiSalvate && (
              <button type="button" className="pc-destiut-buton" onClick={() => setDrepturiDeschise(true)}>
                {t('clienti', 'drepturiButon')}
              </button>
            )}
            <div className={`status-badge ${client.status}`}>
              {client.status === 'activ' ? 'Activ' : 'Inactiv'}
            </div>
          </div>
        </div>
      </div>
      {invitaDeschisa && onInvitat && (
        <InvitaContClient
          client={client}
          onInchide={() => setInvitaDeschisa(false)}
          onInvitat={onInvitat}
        />
      )}
      {telefonDeschis && onDrepturiSalvate && (
        <TelefonClient client={client} onInchide={() => setTelefonDeschis(false)} />
      )}
      {drepturiDeschise && onDrepturiSalvate && (
        <DrepturiClient
          client={client}
          onInchide={() => setDrepturiDeschise(false)}
          onSalvat={onDrepturiSalvate}
        />
      )}

      {/* Cheia Claude a clientului (15 oct 2026): doar super adminul si clientul o vad. */}
      <CheieClaude clientId={client.id} />

      <div className="conturi-rand">
        {conturi === null ? (
          <div className="cont-nume">{t('paginaClient', 'seIncarca')}</div>
        ) : ORDINE_RETELE.map(retea => {
          // TikTok si LinkedIn nu se arata deloc fara cheia lor in mediu, ca sa nu
          // apara un buton care nu duce nicaieri. Daca insa contul E conectat, se
          // arata oricum: altfel ar disparea de pe ecran fara explicatie.
          const cont = gasitePeRetea[retea] || null;
          if (retea === 'tiktok' && !CLIENT_KEY_TIKTOK && !cont) return null;
          if (retea === 'linkedin' && !CLIENT_ID_LINKEDIN && !cont) return null;
          if (retea === 'threads' && !CLIENT_ID_THREADS && !cont) return null;
          if (retea === 'x' && !CLIENT_ID_X && !cont) return null;

          const lista = actiuni(retea, cont);
          const titlu = cont
            ? `${ETICHETE[retea]} · ${cont.nume || ''} — ${textStare(cont.stare, cont.expira_la)}`
            : `${ETICHETE[retea]} · ${t('paginaClient', 'neconectat')}`;

          return (
            <div className="cont" key={retea}>
              <button
                className="cont-buton"
                title={titlu}
                onClick={() => setMeniuDeschis(m => (m === retea ? null : retea))}
              >
                <PozaCont cont={cont} retea={retea} />
                <span className="cont-insigna">{iconRetea ? iconRetea(retea, 13) : null}</span>
                {cont && <span className={`cont-stare ${cont.stare}`} />}
              </button>
              <div className="cont-nume">
                {cont ? (cont.nume || ETICHETE[retea]) : t('paginaClient', 'conecteaza')}
              </div>

              {meniuDeschis === retea && (
                <div className="cont-meniu">
                  <div className="cont-meniu-titlu">
                    {titlu}
                    {retea === 'instagram' && <><br />{t('paginaClient', 'prinFacebook')}</>}
                  </div>
                  {lista.map(a => (
                    <button
                      key={a.id}
                      className={`cont-meniu-actiune ${a.periculos ? 'periculos' : ''}`}
                      onClick={a.onClick}
                    >
                      {a.text}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {(conturiCuProbleme.length > 0 || postariPicate.length > 0 || comentariiPicate.length > 0) && (
        <div className={`pc-destiut ${areCevaGrav ? 'grav' : ''}`}>
          <div className="pc-destiut-titlu">{t('paginaClient', 'deStiut')}</div>

          {conturiCuProbleme.map(c => (
            <div className="pc-destiut-rand" key={`cont-${c.platform}`}>
              <span className="pc-destiut-semn">{c.stare === 'expirat' ? '🔴' : '🟡'}</span>
              <span className="pc-destiut-text">
                {t('paginaClient', c.stare === 'expirat' ? 'contExpirat' : 'contExpira', {
                  retea: ETICHETE[c.platform] || c.platform,
                  nume: c.nume || '',
                })}
              </span>
              {actiuneConectare(c.platform) && (
                <button className="pc-destiut-buton" onClick={actiuneConectare(c.platform)}>
                  {t('paginaClient', 'reconecteazaAcum')}
                </button>
              )}
            </div>
          ))}

          {postariPicate.map(p => (
            <div className="pc-destiut-rand" key={`postare-${p.id}`}>
              <span className="pc-destiut-semn">⚠️</span>
              <span className="pc-destiut-text">
                {t('paginaClient', 'postarePicata', { motiv: p.eroarePublicare })}
              </span>
              <button className="pc-destiut-buton" onClick={() => deschideGrup && deschideGrup(p)}>
                {t('paginaClient', 'deschidePostarea')}
              </button>
            </div>
          ))}

          {comentariiPicate.map(p => (
            <div className="pc-destiut-rand" key={`comentariu-${p.id}`}>
              <span className="pc-destiut-semn">💬</span>
              <span className="pc-destiut-text" style={{ whiteSpace: 'pre-wrap' }}>
                {t('paginaClient', 'comentariuPicat', { motiv: p.comentariuEroare })}
              </span>
              <button className="pc-destiut-buton" onClick={() => comentariuPusDeMana(p)}>
                {t('paginaClient', 'amPusComentariul')}
              </button>
            </div>
          ))}
        </div>
      )}

      <PostariPeTaburi
        postari={postariClient}
        retele={retele}
        statusInfo={statusInfo}
        iconRetea={iconRetea}
        deschideGrup={deschideGrup}
        tabInitial={tabInitial}
        esteClient={esteClient}
        onStergeGrup={onStergeGrup}
      />

      {children}
    </div>
  );
}

// Lista de randuri (un rand = un grup), folosita de pagina clientului si de ecranul
// „Aprobate" al agentiei (16 septembrie 2026). `arataClient` pune numele clientului pe
// rand, pentru ecranele cu mai multi clienti.
export function ListaGrupuri({ postari, retele = [], statusInfo = {}, iconRetea, deschideGrup, arataClient = false, onStergeGrup = null, dupaCreare = false }) {
  const moment = dupaCreare ? momentulCrearii : momentulGrupului;
  const randuri = grupeaza(postari).sort((a, b) => moment(b) - moment(a));
  return (
    <>
      {randuri.length === 0 ? (
        <div className="pc-gol">{t('paginaClient', 'nicioPostare')}</div>
      ) : (
        <div className="pc-lista">
          {randuri.map(grup => {
            const prima = grup[0];
            const text = primaFraza(grup.map(p => p.text).find(x => x && x.trim()));
            // Cate carduri are fiecare status in grup: „2 programate · 1 de aprobat".
            const peStatus = {};
            grup.forEach(p => { peStatus[p.status] = (peStatus[p.status] || 0) + 1; });
            // In fila de ciorne, un rand fara data propusa spune cand a fost scris (in loc de
            // „Fara data", sau de marca veche de publicare ramasa pe o ciorna). Mereu zi.luna si
            // ora de 24, ca in pagina „Asistent AI".
            const scrisa = dupaCreare && !prima.dataProgramata && momentulCrearii(grup)
              ? t('paginaClient', 'scrisaLa', {
                  cand: new Date(momentulCrearii(grup)).toLocaleString('ro-RO', {
                    timeZone: 'Europe/Bucharest', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
                  }),
                })
              : null;
            const data = scrisa || (prima.publicatLa
              ? new Date(prima.publicatLa).toLocaleString(getLocale(), {
                  timeZone: 'Europe/Bucharest', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                })
              : (prima.dataProgramata
                ? `${prima.dataProgramata} · ${String(prima.ora || '').slice(0, 5)}`
                // Aprobata, dar inca fara data: cand a aprobat clientul (cat asteapta).
                : (grup.find(p => p.status === 'aprobat' && p.aprobatLa)
                  ? t('paginaClient', 'aprobatLa', {
                      cand: new Date(grup.find(p => p.status === 'aprobat' && p.aprobatLa).aprobatLa).toLocaleString(getLocale(), {
                        timeZone: 'Europe/Bucharest', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                      }),
                    })
                  : t('paginaClient', 'nedatata'))));

            return (
              <div
                key={prima.grupId || prima.id}
                className="pc-rand"
                role="button"
                tabIndex={0}
                onClick={() => deschideGrup && deschideGrup(prima)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); deschideGrup && deschideGrup(prima); } }}
              >
                <div className="pc-rand-sus">
                  <div className="pc-rand-retele">
                    {arataClient && prima.client && <b style={{ marginRight: 4 }}>{prima.client}</b>}
                    {grup.map(p => {
                      const r = retele.find(x => x.id === p.retea);
                      return (
                        <span className="pc-rand-retea" key={p.id}>
                          {iconRetea ? iconRetea(p.retea, 12) : null}
                          {r ? r.label : p.retea} · {p.format}
                        </span>
                      );
                    })}
                  </div>
                  <div className="pc-rand-data">{data}</div>
                  {onStergeGrup && (
                    <button
                      type="button"
                      className="pc-rand-sterge"
                      title={t('paginaClient', 'stergeRandul')}
                      onClick={e => { e.stopPropagation(); onStergeGrup(grup); }}
                    >
                      🗑
                    </button>
                  )}
                </div>

                <div className="pc-rand-text">{text || t('paginaClient', 'faraText')}</div>

                <div className="pc-rand-jos">
                  {Object.keys(peStatus).map(st => {
                    const si = statusInfo[st] || {};
                    return (
                      <span
                        key={st}
                        className="pc-pastila"
                        style={{ background: si.bg || '#F3F4F6', color: si.culoare || '#4B5563' }}
                      >
                        {peStatus[st]} {t('status', st)}
                      </span>
                    );
                  })}
                  {grup.length > 1 && (
                    <span className="pc-rand-carduri">{t('paginaClient', 'nCarduri', { n: grup.length })}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

// Ecranul „Aprobate" din meniul agentiei: postarile aprobate de client, care asteapta
// sa fie programate sau publicate (cerut de Luminita, 16 septembrie 2026).
export function PostariAprobate({ postari, retele, statusInfo, iconRetea, deschideGrup }) {
  const aprobate = postari.filter(p => p.status === 'aprobat');
  return (
    <div className="pagina">
      <div className="pagina-header">
        <div>
          <h1 className="pagina-titlu">{t('paginaClient', 'aprobateTitlu')}</h1>
          <p className="pagina-subtitlu">{t('paginaClient', 'aprobateSubtitlu')}</p>
        </div>
      </div>
      <ListaGrupuri postari={aprobate} retele={retele} statusInfo={statusInfo} iconRetea={iconRetea} deschideGrup={deschideGrup} arataClient />
    </div>
  );
}

// Tab-urile de postari + lista, comune paginii clientului si paginii Acasa (16 sept 2026).
export function PostariPeTaburi({ postari, retele = [], statusInfo = {}, iconRetea, deschideGrup, tabInitial = 'toate', arataClient = false, esteClient = false, onStergeGrup = null }) {
  const [tabActiv, setTabActiv] = useState(tabInitial);
  const TABURI = taburi(esteClient);
  const tabCurent = TABURI.find(x => x.id === tabActiv) || TABURI[0];
  return (
    <>
      <div className="pc-taburi">
        {TABURI.map(tab => {
          const cate = grupeaza(
            tab.statusuri ? postari.filter(p => tab.statusuri.includes(p.status)) : postari
          ).length;
          return (
            <button
              key={tab.id}
              className={`pc-tab ${tabActiv === tab.id ? 'activ' : ''}`}
              onClick={() => setTabActiv(tab.id)}
            >
              {t('paginaClient', tab.cheie)}
              {cate > 0 && <span className="pc-tab-nr">{cate}</span>}
            </button>
          );
        })}
      </div>

      <ListaGrupuri
        postari={tabCurent.statusuri ? postari.filter(p => tabCurent.statusuri.includes(p.status)) : postari}
        retele={retele}
        statusInfo={statusInfo}
        iconRetea={iconRetea}
        deschideGrup={deschideGrup}
        arataClient={arataClient}
        onStergeGrup={onStergeGrup}
        dupaCreare={tabCurent.id === 'ciorne'}
      />
    </>
  );
}

export default PaginaClient;
